import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Grid,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  LinearProgress,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudUpload,
  Add,
  Delete,
  Launch,
  AttachFile,
  Article,
  Image,
  Description,
  CheckCircle,
  ExpandMore,
  MusicNote,
  Videocam,
  Business,
  Public,
  Share,
  Newspaper,
  Edit
} from '@mui/icons-material';
import axiosinstance from '../../utils/axiosConfig';

interface MarketingPhaseProps {
  resource: any;
  onUpdate: (updatedResource: any) => void;
}

interface MarketingAsset {
  filename: string;
  url: string;
  type: 'pdf' | 'image' | 'document';
  description?: string;
  uploadedAt: Date;
}

const MarketingPhase: React.FC<MarketingPhaseProps> = ({ resource, onUpdate }) => {
  // NEW FILE UPLOAD STATES
  const [marketingAssets, setMarketingAssets] = useState<MarketingAsset[]>(resource?.marketingAssets || []);
  const [marketingEmailContent, setMarketingEmailContent] = useState(resource?.marketingEmailContent || '');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  
  // FILE RENAME DIALOG STATES
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingFileType, setPendingFileType] = useState<'pdf' | 'image' | 'document'>('image');
  const [fileNames, setFileNames] = useState<Record<number, string>>({});
  
  // TYPE DEFINITIONS
  interface SocialMediaState {
    instagram: string;
    facebook: string;
    twitter: string;
    tiktok: string;
    youtube: string;
    linkedin: string;
    artistWebsite: string;
    recordLabelWebsite: string;
    other: string[];
  }

  interface MediaLinksState {
    spotify: string[];
    appleMusic: string[];
    youtubeMusic: string[];
    bandcamp: string[];
    soundcloud: string[];
    wikipedia: string[];
    pressKit: string[];
    bookingAgency: string[];
    management: string[];
    pressArticles: string[];
    other: string[];
  }

  // EXISTING LINK STATES - KEEP AS IS
  const [socialMedia, setSocialMedia] = useState<SocialMediaState>(resource?.socialMedia || {
    instagram: '',
    facebook: '',
    twitter: '',
    tiktok: '',
    youtube: '',
    linkedin: '',
    artistWebsite: '',
    recordLabelWebsite: '',
    other: []
  });
  const [mediaLinks, setMediaLinks] = useState<MediaLinksState>(resource?.mediaLinks || {
    spotify: [],
    appleMusic: [],
    youtubeMusic: [],
    bandcamp: [],
    soundcloud: [],
    wikipedia: [],
    pressKit: [],
    bookingAgency: [],
    management: [],
    pressArticles: [],
    other: []
  });
  
  const [pressReleaseUrl, setPressReleaseUrl] = useState(resource?.pressReleaseUrl || '');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // NEW FILE UPLOAD HANDLER WITH RENAME CAPABILITY
  const handleFileSelection = (files: File[], type: 'pdf' | 'image' | 'document') => {
    if (files.length === 0) return;
    
    // Initialize file names with smart defaults
    const initialNames: Record<number, string> = {};
    files.forEach((file, index) => {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
      // Smart defaults based on file type
      if (type === 'image') {
        initialNames[index] = `${resource?.name || 'Artist'} - Photo ${index + 1}`;
      } else if (type === 'pdf') {
        initialNames[index] = nameWithoutExt.includes('press') ? 
          `${resource?.name || 'Artist'} - Press Release` : 
          `${resource?.name || 'Artist'} - ${nameWithoutExt}`;
      } else {
        initialNames[index] = `${resource?.name || 'Artist'} - ${nameWithoutExt}`;
      }
    });
    
    setPendingFiles(files);
    setPendingFileType(type);
    setFileNames(initialNames);
    setRenameDialogOpen(true);
  };

  const handleRenameConfirm = async () => {
    setRenameDialogOpen(false);
    
    for (let i = 0; i < pendingFiles.length; i++) {
      const file = pendingFiles[i];
      const customName = fileNames[i] || file.name;
      await handleFileUpload(file, pendingFileType, customName);
    }
    
    // Clear state
    setPendingFiles([]);
    setFileNames({});
  };

  const handleFileUpload = async (file: File, type: 'pdf' | 'image' | 'document', customName?: string) => {
    setUploading(true);
    setUploadProgress(0);
    setUploadStatus(`Uploading ${customName || file.name}...`);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', 'marketing');
      formData.append('category', 'marketing');
      formData.append('performanceId', resource.performances?.[0]?._id || resource._id);
      
      // Add custom name if provided
      if (customName) {
        formData.append('customFileName', customName);
      }

      const response = await axiosinstance.post(
        `/api/resources/${resource._id}/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total!);
            setUploadProgress(percentCompleted);
          }
        }
      );

      const newAsset: MarketingAsset = {
        filename: customName || file.name,
        url: response.data.file.url,
        type: type,
        description: `${type} file`,
        uploadedAt: new Date()
      };

      const updatedAssets = [...marketingAssets, newAsset];
      setMarketingAssets(updatedAssets);
      
      setUploadStatus('Upload completed!');
      
      // PERSIST TO DATABASE
      const updatedResource = {
        ...resource,
        marketingAssets: updatedAssets
      };
      
      await axiosinstance.put(`/api/resources/${resource._id}`, {
        marketingAssets: updatedAssets
      });
      
      onUpdate(updatedResource);
      
      setTimeout(() => {
        setUploadStatus('');
        setUploadProgress(0);
      }, 2000);

    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadStatus('Upload failed. Please try again.');
      setTimeout(() => setUploadStatus(''), 3000);
    } finally {
      setUploading(false);
    }
  };

  const removeAsset = async (index: number) => {
    const updatedAssets = marketingAssets.filter((_, i) => i !== index);
    setMarketingAssets(updatedAssets);
    
    try {
      await axiosinstance.put(`/api/resources/${resource._id}`, {
        marketingAssets: updatedAssets
      });
      
      onUpdate({
        ...resource,
        marketingAssets: updatedAssets
      });
    } catch (error) {
      console.error('Error removing asset:', error);
    }
  };

  const saveMarketingContent = async () => {
    try {
      await axiosinstance.put(`/api/resources/${resource._id}`, {
        marketingEmailContent
      });
      
      onUpdate({
        ...resource,
        marketingEmailContent
      });
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Error saving marketing content:', error);
    }
  };

  // EXISTING LINK HANDLERS - KEEP AS IS
  const addMediaLink = (platform: keyof MediaLinksState, url: string) => {
    if (!url.trim()) return;
    
    setMediaLinks((prev: MediaLinksState) => ({
      ...prev,
      [platform]: [...prev[platform], url.trim()]
    }));
  };

  const removeMediaLink = (platform: keyof MediaLinksState, index: number) => {
    setMediaLinks((prev: MediaLinksState) => ({
      ...prev,
      [platform]: prev[platform].filter((_: string, i: number) => i !== index)
    }));
  };

  const saveAllMarketingData = async () => {
    try {
      const updatedResource = {
        ...resource,
        marketingAssets,
        marketingEmailContent,
        pressReleaseUrl,
        socialMedia,
        mediaLinks
      };

      await axiosinstance.put(`/api/resources/${resource._id}`, {
        marketingAssets,
        marketingEmailContent,
        pressReleaseUrl,
        socialMedia,
        mediaLinks
      });

      onUpdate(updatedResource);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving marketing data:', error);
    }
  };

  // NEW FILE UPLOAD ZONES
  const FileUploadZone: React.FC<{
    title: string;
    description: string;
    accept: string;
    type: 'pdf' | 'image' | 'document';
    icon: React.ReactNode;
  }> = ({ title, description, accept, type, icon }) => (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: 3, 
        textAlign: 'center',
        border: '2px dashed #ccc',
        '&:hover': { borderColor: 'primary.main' }
      }}
    >
      <Box sx={{ mb: 2 }}>
        {icon}
      </Box>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>
      <Button
        variant="contained"
        component="label"
        startIcon={<CloudUpload />}
        disabled={uploading}
      >
        Choose Files
        <input
          type="file"
          hidden
          accept={accept}
          multiple={type === 'image'}
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            handleFileSelection(files, type);
          }}
        />
      </Button>
    </Paper>
  );

  // IMPROVED LINK COMPONENT WITH BETTER VISUAL GROUPING
  const LinkGroup: React.FC<{
    title: string;
    icon: React.ReactNode;
    links: string[];
    onAdd: (url: string) => void;
    onRemove: (index: number) => void;
    placeholder: string;
    inputId: string;
  }> = ({ title, icon, links, onAdd, onRemove, placeholder, inputId }) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {icon}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Chip 
          label={links.length} 
          size="small" 
          color={links.length > 0 ? 'primary' : 'default'}
          sx={{ ml: 1 }}
        />
      </Box>
      
      {links.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {links.map((link, index) => (
            <Paper 
              key={index} 
              variant="outlined" 
              sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                '&:hover': { bgcolor: 'grey.50' }
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  flexGrow: 1, 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis',
                  color: 'primary.main',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
                onClick={() => window.open(link.startsWith('http') ? link : `https://${link}`, '_blank')}
              >
                {link}
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => window.open(link.startsWith('http') ? link : `https://${link}`, '_blank')}
                color="primary"
              >
                <Launch />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={() => onRemove(index)}
                color="error"
              >
                <Delete />
              </IconButton>
            </Paper>
          ))}
        </Stack>
      )}
      
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          placeholder={placeholder}
          size="small"
          id={inputId}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const input = e.target as HTMLInputElement;
              if (input.value.trim()) {
                onAdd(input.value.trim());
                input.value = '';
              }
            }
          }}
        />
        <IconButton 
          size="small"
          onClick={() => {
            const input = document.getElementById(inputId) as HTMLInputElement;
            if (input?.value.trim()) {
              onAdd(input.value.trim());
              input.value = '';
            }
          }}
          color="primary"
        >
          <Add />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* EXISTING PRESS RELEASE URL - KEEP AS IS */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Article />
            Press Release Link
          </Typography>
          
          <TextField
            fullWidth
            label="Press Release URL or Document Link"
            value={pressReleaseUrl}
            onChange={(e) => setPressReleaseUrl(e.target.value)}
            placeholder="Link to press release document, webpage, or Google Doc"
            onBlur={() => saveAllMarketingData()}
            InputProps={{
              endAdornment: pressReleaseUrl && (
                <IconButton onClick={() => window.open(pressReleaseUrl, '_blank')}>
                  <Launch />
                </IconButton>
              )
            }}
          />
        </CardContent>
      </Card>

      {/* NEW FILE UPLOADS SECTION */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachFile />
            Marketing Files & Assets
          </Typography>
          
          {uploading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>{uploadStatus}</Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <FileUploadZone
                title="Press Release PDFs"
                description="Upload press releases, media kits, or other PDF documents"
                accept=".pdf"
                type="pdf"
                icon={<Article fontSize="large" color="primary" />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FileUploadZone
                title="Marketing Images"
                description="Upload promotional photos, posters, or graphics"
                accept="image/*"
                type="image"
                icon={<Image fontSize="large" color="primary" />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FileUploadZone
                title="Other Documents"
                description="Upload any other marketing type documents (Word, text files, PDF's etc.)"
                accept=".doc,.docx,.pdf,.txt"
                type="document"
                icon={<Description fontSize="large" color="primary" />}
              />
            </Grid>
          </Grid>

          {/* UPLOADED FILES LIST - BETTER LAYOUT FOR MULTIPLE FILES */}
          {marketingAssets.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Uploaded Files ({marketingAssets.length})
              </Typography>
              
              {/* GROUP BY FILE TYPE */}
              {['pdf', 'image', 'document'].map(fileType => {
                const assetsOfType = marketingAssets.filter(asset => asset.type === fileType);
                if (assetsOfType.length === 0) return null;
                
                return (
                  <Box key={fileType} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', textTransform: 'capitalize' }}>
                      {fileType === 'pdf' ? 'PDFs' : fileType === 'image' ? 'Images' : 'Documents'} ({assetsOfType.length})
                    </Typography>
                    
                    {fileType === 'image' ? (
                      // IMAGE GRID LAYOUT
                      <Grid container spacing={2}>
                        {assetsOfType.map((asset, index) => (
                          <Grid item xs={6} sm={4} md={3} key={index}>
                            <Box sx={{ 
                              position: 'relative',
                              border: '1px solid #ddd',
                              borderRadius: 1,
                              overflow: 'hidden',
                              height: 150
                            }}>
                              <img 
                                src={asset.url} 
                                alt={asset.filename}
                                style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  objectFit: 'cover' 
                                }}
                              />
                              <Box sx={{ 
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                display: 'flex',
                                gap: 0.5
                              }}>
                                <IconButton 
                                  size="small" 
                                  sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                                  onClick={() => window.open(asset.url, '_blank')}
                                >
                                  <Launch fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                                  onClick={() => removeAsset(marketingAssets.indexOf(asset))}
                                  color="error"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                              <Box sx={{ 
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                bgcolor: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                p: 0.5
                              }}>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                  {asset.filename.length > 20 ? asset.filename.substring(0, 20) + '...' : asset.filename}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      // LIST LAYOUT FOR PDFs AND DOCUMENTS
                      <List dense>
                        {assetsOfType.map((asset, index) => (
                          <ListItem key={index} divider sx={{ py: 1 }}>
                            <ListItemText
                              primary={asset.filename}
                              secondary={`${asset.type.toUpperCase()} • ${new Date(asset.uploadedAt).toLocaleDateString()}`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton onClick={() => window.open(asset.url, '_blank')} size="small" sx={{ mr: 1 }}>
                                <Launch />
                              </IconButton>
                              <IconButton onClick={() => removeAsset(marketingAssets.indexOf(asset))} color="error" size="small">
                                <Delete />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* NEW MARKETING CONTENT TEXT */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Marketing Email Content
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste marketing copy, email content, or promotional text here
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={8}
            value={marketingEmailContent}
            onChange={(e) => setMarketingEmailContent(e.target.value)}
            placeholder="Paste your marketing email content, promotional copy, or press release text here..."
            onBlur={saveMarketingContent}
          />
          {saveSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Content saved successfully!
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* IMPROVED SOCIAL MEDIA & PRESS LINKS WITH BETTER GROUPING */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Share />
                Social Media & Websites
              </Typography>
              
              {/* SOCIAL PLATFORMS GROUP */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Social Platforms
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <LinkGroup
                    title="Instagram"
                    icon={<Public color="primary" />}
                    links={socialMedia.instagram ? [socialMedia.instagram] : []}
                    onAdd={(url) => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, instagram: url }));
                      saveAllMarketingData();
                    }}
                    onRemove={() => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, instagram: '' }));
                      saveAllMarketingData();
                    }}
                    placeholder="Add Instagram profile"
                    inputId="instagram-input"
                  />
                  
                  <LinkGroup
                    title="Facebook"
                    icon={<Public color="primary" />}
                    links={socialMedia.facebook ? [socialMedia.facebook] : []}
                    onAdd={(url) => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, facebook: url }));
                      saveAllMarketingData();
                    }}
                    onRemove={() => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, facebook: '' }));
                      saveAllMarketingData();
                    }}
                    placeholder="Add Facebook page"
                    inputId="facebook-input"
                  />
                  
                  <LinkGroup
                    title="Twitter/X"
                    icon={<Public color="primary" />}
                    links={socialMedia.twitter ? [socialMedia.twitter] : []}
                    onAdd={(url) => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, twitter: url }));
                      saveAllMarketingData();
                    }}
                    onRemove={() => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, twitter: '' }));
                      saveAllMarketingData();
                    }}
                    placeholder="Add Twitter/X profile"
                    inputId="twitter-input"
                  />
                  
                  <LinkGroup
                    title="TikTok"
                    icon={<Videocam color="primary" />}
                    links={socialMedia.tiktok ? [socialMedia.tiktok] : []}
                    onAdd={(url) => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, tiktok: url }));
                      saveAllMarketingData();
                    }}
                    onRemove={() => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, tiktok: '' }));
                      saveAllMarketingData();
                    }}
                    placeholder="Add TikTok profile"
                    inputId="tiktok-input"
                  />
                  
                  <LinkGroup
                    title="LinkedIn"
                    icon={<Business color="primary" />}
                    links={socialMedia.linkedin ? [socialMedia.linkedin] : []}
                    onAdd={(url) => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, linkedin: url }));
                      saveAllMarketingData();
                    }}
                    onRemove={() => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, linkedin: '' }));
                      saveAllMarketingData();
                    }}
                    placeholder="Add LinkedIn profile"
                    inputId="linkedin-input"
                  />
                </AccordionDetails>
              </Accordion>

              {/* WEBSITES GROUP */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Websites
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <LinkGroup
                    title="Artist Website"
                    icon={<Public color="primary" />}
                    links={socialMedia.artistWebsite ? [socialMedia.artistWebsite] : []}
                    onAdd={(url) => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, artistWebsite: url }));
                      saveAllMarketingData();
                    }}
                    onRemove={() => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, artistWebsite: '' }));
                      saveAllMarketingData();
                    }}
                    placeholder="Add artist website"
                    inputId="artist-website-input"
                  />
                  
                  <LinkGroup
                    title="Record Label Website"
                    icon={<Business color="primary" />}
                    links={socialMedia.recordLabelWebsite ? [socialMedia.recordLabelWebsite] : []}
                    onAdd={(url) => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, recordLabelWebsite: url }));
                      saveAllMarketingData();
                    }}
                    onRemove={() => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, recordLabelWebsite: '' }));
                      saveAllMarketingData();
                    }}
                    placeholder="Add record label website"
                    inputId="label-website-input"
                  />
                </AccordionDetails>
              </Accordion>

              {/* OTHER SOCIAL MEDIA */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Other Social Media
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <LinkGroup
                    title="Other Social Platforms"
                    icon={<Share color="primary" />}
                    links={socialMedia.other || []}
                    onAdd={(url) => {
                      const currentOther = socialMedia.other || [];
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, other: [...currentOther, url] }));
                      saveAllMarketingData();
                    }}
                    onRemove={(index) => {
                      const newOther = (socialMedia.other || []).filter((_: string, i: number) => i !== index);
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, other: newOther }));
                      saveAllMarketingData();
                    }}
                    placeholder="Add other social media link"
                    inputId="other-social-input"
                  />
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MusicNote />
                Media & Press Links
              </Typography>

              {/* AUDIO CONTENT GROUP */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Audio Content
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <LinkGroup
                    title="Spotify"
                    icon={<MusicNote color="primary" />}
                    links={mediaLinks.spotify || []}
                    onAdd={(url) => {
                      addMediaLink('spotify', url);
                      saveAllMarketingData();
                    }}
                    onRemove={(index) => {
                      removeMediaLink('spotify', index);
                      saveAllMarketingData();
                    }}
                    placeholder="Add Spotify link"
                    inputId="spotify-input"
                  />
                  
                  <LinkGroup
                    title="Apple Music"
                    icon={<MusicNote color="primary" />}
                    links={mediaLinks.appleMusic || []}
                    onAdd={(url) => {
                      addMediaLink('appleMusic', url);
                      saveAllMarketingData();
                    }}
                    onRemove={(index) => {
                      removeMediaLink('appleMusic', index);
                      saveAllMarketingData();
                    }}
                    placeholder="Add Apple Music link"
                    inputId="apple-music-input"
                  />
                  
                  <LinkGroup
                    title="SoundCloud"
                    icon={<MusicNote color="primary" />}
                    links={mediaLinks.soundcloud || []}
                    onAdd={(url) => {
                      addMediaLink('soundcloud', url);
                      saveAllMarketingData();
                    }}
                    onRemove={(index) => {
                      removeMediaLink('soundcloud', index);
                      saveAllMarketingData();
                    }}
                    placeholder="Add SoundCloud link"
                    inputId="soundcloud-input"
                  />
                  
                  <LinkGroup
                    title="Bandcamp"
                    icon={<MusicNote color="primary" />}
                    links={mediaLinks.bandcamp || []}
                    onAdd={(url) => {
                      addMediaLink('bandcamp', url);
                      saveAllMarketingData();
                    }}
                    onRemove={(index) => {
                      removeMediaLink('bandcamp', index);
                      saveAllMarketingData();
                    }}
                    placeholder="Add Bandcamp link"
                    inputId="bandcamp-input"
                  />
                </AccordionDetails>
              </Accordion>

              {/* VISUAL CONTENT GROUP */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Visual Content
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <LinkGroup
                    title="YouTube"
                    icon={<Videocam color="primary" />}
                    links={socialMedia.youtube ? [socialMedia.youtube] : []}
                    onAdd={(url) => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, youtube: url }));
                      saveAllMarketingData();
                    }}
                    onRemove={() => {
                      setSocialMedia((prev: SocialMediaState) => ({ ...prev, youtube: '' }));
                      saveAllMarketingData();
                    }}
                    placeholder="Add YouTube channel"
                    inputId="youtube-input"
                  />
                  
                  <LinkGroup
                    title="YouTube Music"
                    icon={<MusicNote color="primary" />}
                    links={mediaLinks.youtubeMusic || []}
                    onAdd={(url) => {
                      addMediaLink('youtubeMusic', url);
                      saveAllMarketingData();
                    }}
                    onRemove={(index) => {
                      removeMediaLink('youtubeMusic', index);
                      saveAllMarketingData();
                    }}
                    placeholder="Add YouTube Music link"
                    inputId="youtube-music-input"
                  />
                </AccordionDetails>
              </Accordion>

              {/* PRESS & RESEARCH GROUP */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Press & Research
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <LinkGroup
                    title="Press Articles"
                    icon={<Newspaper color="primary" />}
                    links={mediaLinks.pressArticles || []}
                    onAdd={(url) => {
                      addMediaLink('pressArticles', url);
                      saveAllMarketingData();
                    }}
                    onRemove={(index) => {
                      removeMediaLink('pressArticles', index);
                      saveAllMarketingData();
                    }}
                    placeholder="Add press article link"
                    inputId="press-articles-input"
                  />
                  
                  <LinkGroup
                    title="Wikipedia Page"
                    icon={<Article color="primary" />}
                    links={mediaLinks.wikipedia || []}
                    onAdd={(url) => {
                      addMediaLink('wikipedia', url);
                      saveAllMarketingData();
                    }}
                    onRemove={(index) => {
                      removeMediaLink('wikipedia', index);
                      saveAllMarketingData();
                    }}
                    placeholder="Add Wikipedia page"
                    inputId="wikipedia-input"
                  />
                  
                  <LinkGroup
                    title="Press Kit / EPK"
                    icon={<Description color="primary" />}
                    links={mediaLinks.pressKit || []}
                    onAdd={(url) => {
                      addMediaLink('pressKit', url);
                      saveAllMarketingData();
                    }}
                    onRemove={(index) => {
                      removeMediaLink('pressKit', index);
                      saveAllMarketingData();
                    }}
                    placeholder="Add press kit/EPK link"
                    inputId="press-kit-input"
                  />
                </AccordionDetails>
              </Accordion>

              {/* BUSINESS CONTACTS GROUP */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Business Contacts
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <LinkGroup
                    title="Management Company"
                    icon={<Business color="primary" />}
                    links={mediaLinks.management || []}
                    onAdd={(url) => {
                      addMediaLink('management', url);
                      saveAllMarketingData();
                    }}
                    onRemove={(index) => {
                      removeMediaLink('management', index);
                      saveAllMarketingData();
                    }}
                    placeholder="Add management company link"
                    inputId="management-input"
                  />
                  
                  <LinkGroup
                    title="Booking Agency"
                    icon={<Business color="primary" />}
                    links={mediaLinks.bookingAgency || []}
                    onAdd={(url) => {
                      addMediaLink('bookingAgency', url);
                      saveAllMarketingData();
                    }}
                    onRemove={(index) => {
                      removeMediaLink('bookingAgency', index);
                      saveAllMarketingData();
                    }}
                    placeholder="Add booking agency link"
                    inputId="booking-agency-input"
                  />
                </AccordionDetails>
              </Accordion>

              {/* OTHER MEDIA LINKS */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Other Media
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <LinkGroup
                    title="Other Media Links"
                    icon={<Public color="primary" />}
                    links={mediaLinks.other || []}
                    onAdd={(url) => {
                      addMediaLink('other', url);
                      saveAllMarketingData();
                    }}
                    onRemove={(index) => {
                      removeMediaLink('other', index);
                      saveAllMarketingData();
                    }}
                    placeholder="Add other media link"
                    inputId="other-media-input"
                  />
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SAVE BUTTON */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          onClick={saveAllMarketingData}
          startIcon={<CheckCircle />}
          size="large"
        >
          Save All Marketing Data
        </Button>
      </Box>

      {/* FILE RENAME DIALOG */}
      <Dialog 
        open={renameDialogOpen} 
        onClose={() => setRenameDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit />
            Rename Files Before Upload
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Give your files descriptive names so they're easy to find later in the Google Cloud storage. 
            Examples: "Bass Player Photo", "Drummer Action Shot", "Band Group Photo"
          </Typography>
          
          <Stack spacing={3}>
            {pendingFiles.map((file, index) => (
              <Box key={index}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Original: {file.name}
                </Typography>
                <TextField
                  fullWidth
                  label="New File Name"
                  value={fileNames[index] || ''}
                  onChange={(e) => setFileNames(prev => ({ ...prev, [index]: e.target.value }))}
                  placeholder="Enter descriptive name (e.g., 'Bass Player Photo')"
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                        .{file.name.split('.').pop()}
                      </Box>
                    )
                  }}
                />
                
                {/* SMART SUGGESTIONS */}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Quick suggestions:
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    {pendingFileType === 'image' && [
                      'Band Group Photo',
                      'Lead Singer Photo', 
                      'Guitarist Photo',
                      'Drummer Photo',
                      'Bass Player Photo',
                      'Live Performance Photo'
                    ].map(suggestion => (
                      <Chip
                        key={suggestion}
                        label={suggestion}
                        size="small"
                        variant="outlined"
                        onClick={() => setFileNames(prev => ({ ...prev, [index]: suggestion }))}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                    
                    {pendingFileType === 'pdf' && [
                      'Press Release',
                      'Technical Rider',
                      'Biography',
                      'Media Kit'
                    ].map(suggestion => (
                      <Chip
                        key={suggestion}
                        label={suggestion}
                        size="small"
                        variant="outlined"
                        onClick={() => setFileNames(prev => ({ ...prev, [index]: `${resource?.name || 'Artist'} - ${suggestion}` }))}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleRenameConfirm}
            variant="contained"
            disabled={pendingFiles.some((_, index) => !fileNames[index]?.trim())}
          >
            Upload {pendingFiles.length} File{pendingFiles.length > 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketingPhase;